import type { useTranslation } from "react-i18next";

import type { PublicPlanCard } from "@/api/queries/publicPlans";
import { minorToMajor, minorUnitExponent } from "@/lib/money";

/**
 * Shared number formatting and small pieces of the public pricing page
 * (``/pricing-plans``): the plan cards, the recommender and the fee
 * infographic all print the same money the same way.
 */

type T = ReturnType<typeof useTranslation>["t"];

/**
 * Minor units → the visitor's currency.
 *
 * Whole units only: a total printed to the haléř would suggest a precision
 * an estimate does not have, and a subscription has none to show.
 */
export function formatMoney(minor: number, currency: string, lang: string): string {
  const major = minorToMajor(minor, currency);
  try {
    return new Intl.NumberFormat(lang, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(major);
  } catch {
    return `${major.toLocaleString(lang, {
      maximumFractionDigits: minorUnitExponent(currency),
    })} ${currency}`;
  }
}

/**
 * The same amount split into the number and the currency sign.
 *
 * The design sets them in two type sizes ("999" big, "Kč měsíčně" small),
 * and where the sign goes is the locale's call — Czech puts it after the
 * number, English before — so the split comes from ``formatToParts``
 * instead of string surgery on one side of the number.
 */
export function moneyParts(
  minor: number,
  currency: string,
  lang: string,
): { amount: string; currency: string } {
  const major = minorToMajor(minor, currency);
  try {
    const parts = new Intl.NumberFormat(lang, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).formatToParts(major);
    return {
      amount: parts
        .filter((p) => p.type !== "currency")
        .map((p) => p.value)
        .join("")
        .trim(),
      currency: parts.find((p) => p.type === "currency")?.value ?? currency,
    };
  } catch {
    return { amount: Math.round(major).toLocaleString(lang), currency };
  }
}

/**
 * Two amounts in one currency as a range: "999–2 999 Kč" / "CZK 999–2,999".
 *
 * KAN-1942 — the range was glued as ``{bare min}–{formatted max}``, which
 * reads right only where the sign trails the number. English puts it in
 * front and printed "999–CZK 2,999". The locale places the sign: the
 * formatted maximum keeps it, and only its number becomes the range.
 */
export function formatMoneyRange(
  minMinor: number,
  maxMinor: number,
  currency: string,
  lang: string,
): string {
  const lo = moneyParts(minMinor, currency, lang).amount;
  const hi = moneyParts(maxMinor, currency, lang).amount;
  const full = formatMoney(maxMinor, currency, lang);
  return full.includes(hi) ? full.replace(hi, `${lo}–${hi}`) : `${lo}–${full}`;
}

/** Basis points → "0,5" / "0.5" (the percent sign lives in the copy). */
export function formatPercent(bps: number, lang: string): string {
  return new Intl.NumberFormat(lang, { maximumFractionDigits: 2 }).format(bps / 100);
}

/**
 * Minor units → a short axis label: "50 tis.", "1,5 mil." / "50K", "1.5M".
 *
 * ``withSuffix: false`` drops the magnitude word so a run of labels on one
 * axis can print it once ("50 tis. · 100 · 150") the way the design does.
 */
export function formatCompact(
  minor: number,
  currency: string,
  lang: string,
  withSuffix = true,
): { text: string; suffix: string } {
  const parts = new Intl.NumberFormat(lang, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).formatToParts(minorToMajor(minor, currency));
  const suffix = parts.find((p) => p.type === "compact")?.value ?? "";
  const text = parts
    .filter((p) => withSuffix || (p.type !== "compact" && p.type !== "literal"))
    .map((p) => p.value)
    .join("")
    .trim();
  return { text, suffix };
}

/** The subscription for one month under the chosen billing, minor units. */
export function monthlyMinor(
  plan: PublicPlanCard,
  billing: "monthly" | "annual",
): number | null {
  if (billing === "annual" && plan.price_annual_cents != null) {
    return Math.round(plan.price_annual_cents / 12);
  }
  return plan.price_monthly_cents;
}

/**
 * Whole months an annual subscription saves over twelve monthly ones.
 *
 * Derived, never spelled: #1274 took a hardcoded "−2 měsíce" off this page,
 * and the design's "2 měsíce zdarma" comes back only as what the two prices
 * actually say.
 */
export function freeMonths(plan: PublicPlanCard): number {
  const monthly = plan.price_monthly_cents;
  const annual = plan.price_annual_cents;
  if (!monthly || annual == null) return 0;
  return Math.max(0, Math.round(12 - annual / monthly));
}

/** Where a tier's call to action leads and what it says. */
export function planCta(
  plan: PublicPlanCard,
  name: string,
  lang: string,
  t: T,
  short = false,
): { label: string; to: string } {
  // A tier without a public price is quoted, so it routes to sales.
  if (plan.price_monthly_cents == null) {
    return {
      label: t(short ? "pricingPlans.ctaContactShort" : "pricingPlans.ctaContact"),
      to: `/${lang}/contact?topic=${plan.tier}`,
    };
  }
  return {
    label:
      plan.price_monthly_cents === 0
        ? t("pricingPlans.ctaStartFree")
        : t("pricingPlans.ctaChoose", { plan: name }),
    to: `/${lang}/sign-up-shop?plan=${plan.tier}`,
  };
}

export function CheckIcon({
  size = 14,
  strokeWidth = 2.6,
}: {
  size?: number;
  strokeWidth?: number;
}): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function ArrowIcon(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h13M13 6l6 6-6 6" />
    </svg>
  );
}

export function SectionHead({
  eyebrow,
  heading,
  lead,
  headingId,
}: {
  eyebrow: string;
  heading: string;
  lead: string;
  headingId?: string;
}): JSX.Element {
  return (
    <div className="shead">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h2 id={headingId}>{heading}</h2>
      </div>
      <p>{lead}</p>
    </div>
  );
}
